import { DeleteOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Checkbox, Collapse, Empty, List, Modal, Space, Spin, Tooltip, Typography, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { archiveCustomerArtwork, getCustomerArtworks } from '../../../api/customer/customer-details.js';
import { getItemCustomerArtworks, linkLibraryArtworkToItem } from '../../../api/project-item/project-item.js';
import { generateRouteUrl } from '../../../library/constants/index.js';
import { getFileExtension, getSecuredFileUrl, isImagePreviewType } from '../../../library/utilities/urls.js';
import Confirm from '../../message/Confirm.jsx';

// Company artwork library: reusable original artwork supplied by the customer (logo, brand files).
// Leoprinting proofs are product-specific and never part of it.

const getArtworkUrl = (artwork) => getSecuredFileUrl(`document/customers/${artwork.customer_id}/${artwork.url}`)

// Items in the order flow open in the Order Manager, others on their project page
const getSourceItemUrl = ({ source_item }) => {
    if (!source_item) return null
    return source_item.order_status
        ? generateRouteUrl('OrderManagerDetailsPage', { itemId: source_item.id })
        : generateRouteUrl('ProjectUpdate', { projectId: source_item.project_id })
}

const downloadArtwork = async (artwork) => {
    try {
        const response = await fetch(getArtworkUrl(artwork), { credentials: 'include' })
        if (!response.ok) throw new Error('Unable to download file')
        const url = URL.createObjectURL(await response.blob())
        const link = document.createElement('a')
        link.href = url
        link.download = artwork.name
        link.click()
        URL.revokeObjectURL(url)
    }
    catch (error) {
        message.error(error.message)
    }
}

const ArtworkThumb = ({ artwork }) => (
    isImagePreviewType(getFileExtension(artwork.url))
        ? <img src={getArtworkUrl(artwork)} alt={artwork.name} style={{ width: 56, height: 56, objectFit: 'contain', background: '#fff', border: '1px solid #eee', borderRadius: 8 }} />
        : <img src={`/icons/filetypes/${getFileExtension(artwork.url) === 'pdf' ? 'pdf' : 'file'}.svg`} alt={artwork.name} style={{ width: 56, height: 56 }} />
)
ArtworkThumb.propTypes = {
    artwork: PropTypes.object.isRequired,
}

const ArtworkDescription = ({ artwork }) => (
    <List.Item.Meta
        avatar={<ArtworkThumb artwork={artwork} />}
        title={artwork.name}
        description={<>
            {artwork.comment && <div>{artwork.comment}</div>}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Added {new Date(artwork.createdAt).toLocaleDateString()}{artwork.creator?.name ? ` by ${artwork.creator.name}` : ''}
                {!!artwork.source_item_id && <> · from {getSourceItemUrl(artwork)
                    ? <Link to={getSourceItemUrl(artwork)} onClick={(e) => e.stopPropagation()}>item {artwork.source_item_id}</Link>
                    : `item ${artwork.source_item_id}`}</>}
            </Typography.Text>
        </>}
    />
)
ArtworkDescription.propTypes = {
    artwork: PropTypes.object.isRequired,
}

/* Company page section: read-only list. Artwork enters the library from an item. Hidden when empty. */
export const CompanyArtworkLibrary = ({ customerId }) => {
    const [artworks, setArtworks] = useState([])

    useEffect(() => {
        getCustomerArtworks(customerId)
            .then(setArtworks)
            .catch((error) => message.error(error.message))
    }, [customerId])

    const handleArchive = async (artworkId) => {
        try {
            message.success(await archiveCustomerArtwork(customerId, artworkId))
            setArtworks(list => list.filter(({ id }) => id !== artworkId))
        }
        catch (error) {
            message.error(error.message)
        }
    }

    if (!artworks.length) return null

    return (
        <Collapse defaultActiveKey={['1']} expandIconPosition="end">
            <Collapse.Panel header={<><img src="/icons/headericons/documents.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Company Artwork ({artworks.length})</>} key="1">
                <List
                    dataSource={artworks}
                    renderItem={(artwork) => (
                        <List.Item extra={
                            <Space size={4}>
                                <Tooltip title="View"><Button type="text" href={getArtworkUrl(artwork)} target="_blank" icon={<EyeOutlined />} /></Tooltip>
                                <Tooltip title="Download"><Button type="text" onClick={() => downloadArtwork(artwork)} icon={<DownloadOutlined />} /></Tooltip>
                                <Tooltip title="Remove from library"><span><Confirm type="text" danger title="Remove artwork" content="The artwork is removed from the library. Items that already use it keep their copy." okText="Remove" onConfirm={() => handleArchive(artwork.id)} icon={<DeleteOutlined />} /></span></Tooltip>
                            </Space>
                        }>
                            <ArtworkDescription artwork={artwork} />
                        </List.Item>
                    )}
                />
            </Collapse.Panel>
        </Collapse>
    )
}
CompanyArtworkLibrary.propTypes = {
    customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

/* Item: pick artwork from the company library and link it to the item */
export const CompanyArtworkPicker = ({ itemId, open, onClose, onLinked }) => {
    const [artworks, setArtworks] = useState([])
    const [selected, setSelected] = useState([])
    const [loading, setLoading] = useState(false)
    const [isLinking, setIsLinking] = useState(false)

    useEffect(() => {
        if (!open) return
        setSelected([])
        setLoading(true)
        getItemCustomerArtworks(itemId)
            .then(setArtworks)
            .catch((error) => message.error(error.message))
            .finally(() => setLoading(false))
    }, [open, itemId])

    const toggle = (id) => setSelected(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id])

    const handleLink = async () => {
        setIsLinking(true)
        try {
            message.success(await linkLibraryArtworkToItem(itemId, selected))
            onLinked?.()
            onClose()
        }
        catch (error) {
            message.error(error.message)
        }
        setIsLinking(false)
    }

    return (
        <Modal
            title="Company Artwork Library"
            open={open}
            onCancel={onClose}
            width={640}
            okText="Add to item"
            onOk={handleLink}
            okButtonProps={{ disabled: !selected.length, loading: isLinking }}
            destroyOnClose
        >
            <Spin spinning={loading}>
                <List
                    dataSource={artworks}
                    locale={{ emptyText: <Empty description="No company artwork yet. Customer artwork uploaded on any item of this company appears here." /> }}
                    renderItem={(artwork) => (
                        <List.Item
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggle(artwork.id)}
                            actions={[
                                <Button key="view" href={getArtworkUrl(artwork)} target="_blank" icon={<EyeOutlined />} onClick={(e) => e.stopPropagation()}>View</Button>
                            ]}
                        >
                            <Checkbox checked={selected.includes(artwork.id)} style={{ marginRight: 12 }} />
                            <ArtworkDescription artwork={artwork} />
                        </List.Item>
                    )}
                />
            </Spin>
        </Modal>
    )
}
CompanyArtworkPicker.propTypes = {
    itemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onLinked: PropTypes.func,
}
